import { CircularProgress, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Field, Form } from 'react-final-form';
import {
  MatchingRoundDetails,
  MatchingRoundStatus,
} from '../../api/matching-rounds';
import { formatDateToRFC } from '../../utils/date';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import { BeforeUnload, Button, FormGroup, StickyContent } from '../common';
import { FormDateInput, FormSelect, TextFieldWrapper } from './wrappers';

interface MatchingRoundDetailsFormProps {
  initialValues?: Partial<MatchingRoundDetails>;
  onSubmit: (values: MatchingRoundDetails) => Promise<void>;
  loading?: boolean;
}

interface FormValues {
  title: string;
  creationDate: string;
  description: string;
  status: string;
}

type FormErrors = Partial<Record<keyof FormValues, string>>;

const STATUS_OPTIONS = [
  { label: 'Created', value: MatchingRoundStatus.CREATED },
  { label: 'Planned', value: MatchingRoundStatus.PLANNED },
  { label: 'In Review', value: MatchingRoundStatus.IN_APPROVAL },
  { label: 'Finalized', value: MatchingRoundStatus.FINALIZED },
];

const MAX_TITLE_LENGTH = 512;
const MAX_DESCRIPTION_LENGTH = 1000;

function validateForm(values: FormValues): FormErrors {
  const errors: FormErrors = {};

  if (!values.title || !values.title.trim()) {
    errors.title = 'Required';
  } else if (values.title.length > MAX_TITLE_LENGTH) {
    errors.title = `Max ${MAX_TITLE_LENGTH} characters`;
  }

  if (values.description && values.description.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `Max ${MAX_DESCRIPTION_LENGTH} characters`;
  }

  return errors;
}

const useStyles = makeStyles({
  formFields: {
    maxWidth: 600,
    '& .MuiSelect-outlined': {
      height: 19,
      lineHeight: '19px',
      paddingTop: '18.5px',
      paddingBottom: '18.5px',
    },
  },
  readOnlyGroup: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  actionsBlock: {
    marginTop: 56,
  },
});

function MatchingRoundDetailsForm({
  initialValues,
  onSubmit,
  loading,
}: MatchingRoundDetailsFormProps) {
  const classes = useStyles();

  const defaultValues: FormValues = {
    title: initialValues?.title || '',
    creationDate: initialValues?.creationDate || formatDateToRFC(new Date()),
    description: initialValues?.description || '',
    status: initialValues?.status || MatchingRoundStatus.CREATED,
  };

  const handleSubmit = async (values: FormValues) => {
    await onSubmit({
      title: values.title.trim(),
      creationDate: values.creationDate,
      description: values.description.trim(),
      status: values.status as MatchingRoundStatus,
    });
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={defaultValues}
      keepDirtyOnReinitialize
      validate={validateForm}
      subscription={{ dirty: true, submitting: true, hasValidationErrors: true }}
      render={({ handleSubmit: formHandleSubmit, dirty, submitting, form, hasValidationErrors }) => (
        <form
          onSubmit={formHandleSubmit}
          data-testid='matching-round-details-form'
        >
          <BeforeUnload
            when={dirty && !loading}
            title='Leave the page'
            body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
            disabled={loading}
            confirmButtonRenderer={({ onConfirm }) => (
              <Button
                variant='outlined'
                disabled={hasValidationErrors || loading || submitting}
                onClick={async () => {
                  await form.submit();
                  onConfirm();
                }}
                data-testid='save-and-leave'>
                {submitting ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Save the changes'
                )}
              </Button>
            )}
          />
          <div className={classes.formFields}>
            <FormGroup mobile>
              <Field
                name='title'
                label='Title*'
                component={TextFieldWrapper}
                testid='field-title'
              />
            </FormGroup>
            <FormGroup mobile>
              <Field
                name='description'
                label='Description'
                component={TextFieldWrapper}
                multiline
                rows={4}
                testid='field-description'
              />
            </FormGroup>
            <FormGroup mobile>
              <Field
                name='creationDate'
                label='Creation Date'
                component={FormDateInput}
                isReadOnly
                testid='field-creation-date'
              />
              <Field
                name='status'
                label='Status'
                component={FormSelect}
                options={STATUS_OPTIONS}
                testid='field-status'
              />
            </FormGroup>
          </div>
          <div className={classes.actionsBlock}>
            <StickyContent>
              <Button
                data-testid='button-save'
                className={CLASS_TRACKING.INTERNAL_ACTION}
                onClick={formHandleSubmit}
                disabled={loading || submitting}
                startIcon={<CheckIcon />}
              >
                {loading || submitting ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Save'
                )}
              </Button>
            </StickyContent>
          </div>
        </form>
      )}
    />
  );
}

export default MatchingRoundDetailsForm;
